import bind from 'bind-decorator';
import type { IOrganizationPractitionerOptions } from './ServiceOrganizationProviderSelection';
import { translateKey } from '../../translate';
import {
    Feedback,
    IconButton,
    InputRadio,
    InputText,
    ListTable,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
    ThirdHeader,
} from './common';
import { Component } from './Component';
import { FormRow } from './forms/FormRow';
import { InputContainer } from './forms/InputContainer';
import { StackedFormGroup } from './forms/StackedFormGroup';
import { ListPager } from './ListPager';

interface IServiceOrganizationProviderSearchProps {
    clearSelectedItem: () => Promise<void>;
    error?: string;
    initialOrganizationId: string | null;
    initialValue?: string;
    onChange: (restul: { label?: string; value?: string }) => Promise<void>;
    onSearch: (searchText?: string, page?: number) => Promise<void>;
    organizations?: IOrganizationPractitionerOptions[];
    placeholder: string;
    total: number;
}

interface IServiceOrganizationProviderSearchState {
    page: number;
    searchText?: string;
    showResult?: boolean;
}

export class ServiceOrganizationProviderSelectionSearch extends Component<
    IServiceOrganizationProviderSearchProps,
    IServiceOrganizationProviderSearchState
> {
    constructor(props: IServiceOrganizationProviderSearchProps) {
        super(props);

        this.state = {
            page: 1,
            searchText: this.props.initialValue,
        };
    }

    public componentDidUpdate(prevProps: IServiceOrganizationProviderSearchProps) {
        if (this.props.initialValue && this.props.initialValue !== prevProps.initialValue) {
            this.setState({ searchText: this.props.initialValue });
        }
    }

    public render() {
        return (
            <>
                <FormRow hasStackedFormGroups>
                    <StackedFormGroup>
                        <InputText
                            onChange={async (value) => {
                                await this.setState({ searchText: value, showResult: false });
                                await this.props.clearSelectedItem();
                            }}
                            onEnter={async () => {
                                await this.props.onSearch(this.state.searchText, 1);
                                await this.setState({ showResult: true });
                            }}
                            placeholder={this.props.placeholder}
                            value={this.state.searchText || ''}
                        />
                    </StackedFormGroup>
                    <StackedFormGroup colSizeAuto>
                        <IconButton
                            icon="search"
                            inputType="button"
                            onClick={async () => {
                                if (this.state.searchText) {
                                    await this.props.onSearch(this.state.searchText, 1);
                                    await this.setState({ showResult: true });
                                }
                            }}
                        />
                    </StackedFormGroup>
                </FormRow>

                <Feedback error={this.props.error} />

                {this.state.showResult && this.props.organizations && (
                    <>
                        <FormRow>
                            <InputContainer>
                                {this.props.organizations.length === 0 ? (
                                    <ThirdHeader text={translateKey('organization.noSearchResultOrganization')} />
                                ) : (
                                    <ListTable>
                                        <TableHead>
                                            <TableRow>
                                                <TableHeader className="input-spacing"> </TableHeader>
                                                <TableHeader>
                                                    {translateKey('waitingTime.applicationWaitingTime')}
                                                </TableHeader>
                                                <TableHeader>
                                                    {translateKey('waitingTime.treatmentWaitingTime')}
                                                </TableHeader>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {this.props.organizations.map((organization) => (
                                                <TableRow key={organization.value}>
                                                    <TableData>
                                                        <InputRadio
                                                            label={organization.label}
                                                            name="organization.value"
                                                            onChange={async () =>
                                                                await this.props.onChange({
                                                                    value: organization.value,
                                                                    label: organization.label,
                                                                })
                                                            }
                                                            onValue={this.props.initialOrganizationId || ''}
                                                            value={organization.value}
                                                        />
                                                    </TableData>
                                                    <TableData>
                                                        {organization.applicationWaitingTime !== null &&
                                                        organization.applicationWaitingTime !== 0
                                                            ? `${organization.applicationWaitingTime} ${translateKey(
                                                                  'waitingTime.weeks'
                                                              )}`
                                                            : translateKey('common.none')}
                                                    </TableData>
                                                    <TableData>
                                                        {organization.treatmentWaitingTime !== null &&
                                                        organization.treatmentWaitingTime !== 0
                                                            ? `${organization.treatmentWaitingTime} ${translateKey(
                                                                  'waitingTime.weeks'
                                                              )}`
                                                            : translateKey('common.none')}
                                                    </TableData>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </ListTable>
                                )}
                            </InputContainer>
                        </FormRow>
                        <FormRow>
                            <InputContainer>
                                <ListPager
                                    itemsPerPage={10}
                                    onChangePage={this.handleNavigation}
                                    page={this.state.page}
                                    total={this.props.total}
                                />
                            </InputContainer>
                        </FormRow>
                    </>
                )}
            </>
        );
    }

    @bind
    private async handleNavigation(page: number) {
        await this.setState({ page });
        await this.props.onSearch(this.state.searchText, this.state.page);
    }
}
